import type { Block, Check, FeatureState, ImplementationStatus } from '../../docs/types';

/**
 * Renderers for one documentation block, and the status badges.
 *
 * The `todo` block is styled to be impossible to skim past. Its whole reason for
 * existing is that an unfinished feature should look unfinished on the page, not
 * be described in the same voice as one that works.
 */

const STATE_STYLE: Record<FeatureState, { label: string; className: string }> = {
  shipped: {
    label: 'Shipped',
    className: 'border-accent/40 bg-accent-soft text-accent',
  },
  partial: {
    label: 'Partial',
    className: 'border-amber-400/40 bg-amber-400/10 text-amber-300',
  },
  missing: {
    label: 'Not usable yet',
    className: 'border-red-400/40 bg-red-500/10 text-red-300',
  },
};

export function StateBadge({ state }: { state: FeatureState }) {
  const style = STATE_STYLE[state];
  return (
    <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${style.className}`}>
      {style.label}
    </span>
  );
}

const CHECK_STYLE: Record<Check, { label: string; className: string }> = {
  yes: { label: 'Yes', className: 'text-accent' },
  no: { label: 'No', className: 'text-red-300' },
  partial: { label: 'Partial', className: 'text-amber-300' },
  'n/a': { label: '—', className: 'text-neutral-600' },
};

export function CheckCell({ value }: { value: Check }) {
  const style = CHECK_STYLE[value];
  return <span className={style.className}>{style.label}</span>;
}

/** The per-feature audit panel, shown under the title on any page that has one. */
export function StatusPanel({ status }: { status: ImplementationStatus }) {
  const rows: [string, Check][] = [
    ['Frontend', status.frontend],
    ['Backend', status.backend],
    ['Persistence', status.persistence],
    ['Authentication', status.auth],
    ['Permissions', status.permissions],
    ['Error handling', status.errorHandling],
    ['Tests', status.tests],
  ];

  return (
    <section className="rounded-xl border border-line bg-panel-raised p-4">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
        Implementation status
      </h2>
      <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1.5 sm:grid-cols-4">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-baseline justify-between gap-2 text-sm">
            <dt className="text-neutral-500">{label}</dt>
            <dd className="font-medium">
              <CheckCell value={value} />
            </dd>
          </div>
        ))}
      </dl>

      {status.gaps && status.gaps.length > 0 && (
        <div className="mt-4 border-t border-line pt-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-300">
            Known gaps
          </p>
          <ul className="mt-2 space-y-1.5">
            {status.gaps.map((gap, i) => (
              <li key={i} className="flex gap-2 text-sm text-neutral-300">
                <span aria-hidden className="text-amber-400">
                  •
                </span>
                <span>{gap}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

const NOTE_STYLE = {
  info: 'border-sky-400/30 bg-sky-400/5 text-sky-100',
  warn: 'border-amber-400/30 bg-amber-400/5 text-amber-100',
  danger: 'border-red-400/30 bg-red-500/5 text-red-100',
} as const;

export function BlockView({ block }: { block: Block }) {
  switch (block.kind) {
    case 'heading':
      return (
        <h2 className="mt-8 scroll-mt-24 text-base font-semibold text-neutral-100">
          {block.body}
        </h2>
      );

    case 'text':
      return <p className="leading-relaxed text-neutral-300">{block.body}</p>;

    case 'steps':
      return (
        <ol className="space-y-2">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-panel-raised text-[11px] font-medium text-neutral-400">
                {i + 1}
              </span>
              <span className="leading-relaxed text-neutral-300">{item}</span>
            </li>
          ))}
        </ol>
      );

    case 'list':
      return (
        <ul className="space-y-1.5">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-2.5">
              <span aria-hidden className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-neutral-600" />
              <span className="leading-relaxed text-neutral-300">{item}</span>
            </li>
          ))}
        </ul>
      );

    case 'table':
      return (
        // Wide tables scroll inside their own container rather than pushing the
        // page sideways on a narrow screen.
        <div className="overflow-x-auto rounded-lg border border-line">
          <table className="w-full min-w-[32rem] text-left text-sm">
            <thead className="bg-panel-raised">
              <tr>
                {block.headers.map((header) => (
                  <th key={header} className="px-3 py-2 font-medium text-neutral-400">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i} className="border-t border-line align-top">
                  {row.map((cell, j) => (
                    <td key={j} className="px-3 py-2 text-neutral-300">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'code':
      return (
        <pre className="overflow-x-auto rounded-lg border border-line bg-panel-raised p-3 text-xs leading-relaxed">
          <code className="font-mono text-neutral-200">{block.body}</code>
        </pre>
      );

    case 'note':
      return (
        <div className={`rounded-lg border px-4 py-3 text-sm leading-relaxed ${NOTE_STYLE[block.tone]}`}>
          {block.body}
        </div>
      );

    case 'todo':
      return (
        <div className="rounded-lg border border-dashed border-amber-400/50 bg-amber-400/5 px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-300">
            Not built yet
          </p>
          <p className="mt-1 text-sm leading-relaxed text-amber-100">{block.body}</p>
        </div>
      );

    case 'errors':
      return (
        <div className="space-y-2">
          {block.rows.map((row) => (
            <div key={row.code} className="rounded-lg border border-line bg-panel-raised p-3">
              <code className="font-mono text-xs text-red-300">{row.code}</code>
              <p className="mt-1.5 text-sm text-neutral-300">{row.cause}</p>
              <p className="mt-1 text-sm text-neutral-400">
                <span className="text-neutral-500">Fix: </span>
                {row.fix}
              </p>
            </div>
          ))}
        </div>
      );

    case 'screenshot':
      return (
        // A labelled placeholder, not a broken image: a missing asset should be
        // visible as missing rather than rendering as an empty gap.
        <figure className="rounded-lg border border-dashed border-line bg-panel-raised p-6 text-center">
          <p className="text-xs uppercase tracking-wide text-neutral-600">Screenshot</p>
          <figcaption className="mt-1 text-sm text-neutral-500">{block.caption}</figcaption>
        </figure>
      );
  }
}
